import { memo, useMemo } from 'react'
import type { ToolCall } from '../../../../types'
import { BUILTIN_TOOLS } from '../../../../integrations/utils'
import { AnimatePresence, motion, type Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { StagedActionsRenderer, type StagedActionSnapshot } from './staged-actions-renderer'
import { parseToolCallResultCached } from '../tools/tool-call-utils'
import { useLatestStagedActionContext } from '../../latest-staged-actions-context'
import { askUserStagedActionSnapshot } from '../../../../integrations/ask-user-inline'

const fadeInInitial = { opacity: 0 }
const fadeInAnimate = { opacity: 1 }
const fadeInTransition: Transition = { duration: 0.3 }

interface StagedActionsSectionProps {
  toolCalls: Array<ToolCall>
  groupToolCalls?: Array<ToolCall>
  isTurnActive: boolean
  onStagedActionClick: (actionId: string, actionType: string) => void
  onStagedActionCreated?: () => void
}

export const StagedActionsSection = memo(function StagedActionsSection({
  toolCalls,
  groupToolCalls,
  isTurnActive,
  onStagedActionClick,
  onStagedActionCreated,
}: StagedActionsSectionProps) {
  const latestStagedActionByActionId = useLatestStagedActionContext()
  const allToolCalls = useMemo(() => [...toolCalls, ...(groupToolCalls || [])], [toolCalls, groupToolCalls])

  const stagedActions = useMemo(() => {
    const filtered = allToolCalls.filter((toolCall) => {
      if (toolCall.toolName === BUILTIN_TOOLS.CREATE_STAGED_ACTION) return true
      if (toolCall.result) {
        const r = parseToolCallResultCached(toolCall)
        if (r?.stagedAction || r?.stagedActions) return true
      }
      return false
    })

    // Deduplicate: keep only the last tool call per action ID
    const latestByActionId = new Map<string, ToolCall>()
    for (const toolCall of filtered) {
      const r = parseToolCallResultCached(toolCall)
      const actions: Array<{ id: string }> = r?.stagedActions ?? (r?.stagedAction ? [r.stagedAction] : [])
      if (actions.length > 0) {
        for (const action of actions) {
          latestByActionId.set(action.id, toolCall)
        }
      } else {
        // tool call has no parsed result yet (loading/failed) — key by tool call id
        latestByActionId.set(toolCall.id, toolCall)
      }
    }
    // latestByActionId can map multiple action IDs to the same tool call —
    // deduplicate by tool call id before returning
    const seen = new Set<string>()
    return Array.from(latestByActionId.values()).filter((tc) => {
      if (seen.has(tc.id)) return false
      seen.add(tc.id)
      return true
    })
  }, [allToolCalls])

  const answerableActions = useMemo(() => {
    const latestByActionId = new Map<string, StagedActionSnapshot>()
    for (const toolCall of allToolCalls) {
      const action = askUserStagedActionSnapshot(toolCall)
      if (action) {
        latestByActionId.set(action.id, action)
      }
    }
    return Array.from(latestByActionId.values())
  }, [allToolCalls])

  if (stagedActions.length === 0 && answerableActions.length === 0) {
    return null
  }

  return (
    <>
      {stagedActions.map((toolCall, index) => {
        const isFailed = toolCall.status === 'failed'
        const isToolLoading = toolCall.status === 'executing' || toolCall.status === 'ready'
        const isLast = index === stagedActions.length - 1

        if (isToolLoading || isTurnActive) return null

        if (isFailed) {
          return (
            <AnimatePresence key={toolCall.id}>
              <motion.div
                initial={fadeInInitial}
                animate={fadeInAnimate}
                transition={fadeInTransition}
                className={twMerge('mt-2', isLast && 'mb-2')}
              >
                <div className="border-danger bg-danger-subtle text-danger-subtle rounded-lg border p-3 text-sm">
                  Failed to create action
                </div>
              </motion.div>
            </AnimatePresence>
          )
        }

        const result = parseToolCallResultCached(toolCall)
        const rawActions: Array<{ id: string }> =
          result?.stagedActions || (result?.stagedAction ? [result.stagedAction] : [])

        // Filter out actions that have been superseded by a newer tool call.
        // Unlike a whole-tool-call gate, this preserves sibling actions that
        // were not re-created by the newer tool call.
        const actions =
          latestStagedActionByActionId && latestStagedActionByActionId.size > 0 && rawActions.length > 0
            ? rawActions.filter((a) => latestStagedActionByActionId.get(a.id) === toolCall.id)
            : rawActions

        // If this tool call had actions but all of them are now owned by newer
        // tool calls, skip rendering entirely.
        if (rawActions.length > 0 && actions.length === 0) return null

        if ((result?.success || actions.length > 0) && actions.length > 0) {
          return (
            <AnimatePresence key={toolCall.id}>
              <motion.div initial={fadeInInitial} animate={fadeInAnimate} transition={fadeInTransition}>
                <StagedActionsRenderer
                  actions={actions}
                  isLast={isLast}
                  onStagedActionClick={onStagedActionClick}
                  onStagedActionCreated={onStagedActionCreated}
                  toolCallView={toolCall.view}
                />
              </motion.div>
            </AnimatePresence>
          )
        }
        return null
      })}
      {answerableActions.length > 0 && !isTurnActive && (
        <AnimatePresence>
          <motion.div initial={fadeInInitial} animate={fadeInAnimate} transition={fadeInTransition}>
            <StagedActionsRenderer
              actions={answerableActions}
              isLast={true}
              onStagedActionClick={onStagedActionClick}
              onStagedActionCreated={onStagedActionCreated}
              answerablePresentation="resolved"
            />
          </motion.div>
        </AnimatePresence>
      )}
    </>
  )
})
